import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import ApplyLeaveAdminActionType from './ApplyLeaveAdmin.types';
import { applyLeaveAdminAPI, getAllEmployeesAdminAPI } from './ApplyLeaveAdmin.services';
import {
	applyLeaveAdminFailure,
	applyLeaveAdminSuccess,
	getAllEmployeesAdminSuccess,
	getAllEmployeesAdminFailure,
} from './ApplyLeaveAdmin.action';
import AlertManager from '../../utils/alertManager';
import { encryptData } from '../_common/enode-decode';

interface ApplyLeaveAdminData {
	employeeID: string;
	fromDate: string;
	toDate: string;
	leaveDuration: number;
	typeOfLeave: string;
	reason: string;
	createdOn: string;
	status: string;
	organisationID: number;
	createdBy?: string;
}

interface OrganisationData {
	organisationID: number;
}

export function* applyLeaveAdmin({
	payload,
}: {
	payload: ApplyLeaveAdminData;
}): Generator<any, void, any> {
	try {
		if (process.env.NODE_ENV === 'development') console.log('Apply Leave Admin saga started with payload:', payload);
		const encryptedPayload = encryptData(
			payload,
			ApplyLeaveAdminActionType.APPLY_LEAVE_ADMIN_KEY,
		);
		const response = yield call(applyLeaveAdminAPI as any, encryptedPayload);
		const responseData = response?.data;

		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}

		if (responseData?.status === 'error') {
			yield put(applyLeaveAdminFailure(responseData));
		} else {
			yield put(applyLeaveAdminSuccess(responseData));
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in applyLeaveAdmin saga:', error);
		if (error?.response?.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		yield put(applyLeaveAdminFailure(error?.response?.data || error));
	}
}

export function* getAllEmployeesAdmin({
	payload,
}: {
	payload: OrganisationData;
}): Generator<any, void, any> {
	try {
		if (process.env.NODE_ENV === 'development') console.log('Get All Employees Admin saga started with payload:', payload);
		const encryptedPayload = encryptData(
			payload,
			ApplyLeaveAdminActionType.GET_ALL_EMPLOYEES_ADMIN_KEY,
		);
		const response = yield call(getAllEmployeesAdminAPI as any, encryptedPayload);
		const responseData = response?.data;

		if (responseData?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}

		// Check if response has the correct structure (with 'response' property for decryption)
		if (!responseData || !responseData.response) {
			if (process.env.NODE_ENV === 'development') console.error('Invalid response structure:', responseData);
			yield put(getAllEmployeesAdminFailure({
				status: 'error',
				message_text: 'Invalid response from server. Please try again.'
			}));
			return;
		}

		if (responseData?.status === 'error') {
			yield put(getAllEmployeesAdminFailure(responseData));
		} else {
			yield put(getAllEmployeesAdminSuccess(responseData));
		}
	} catch (error: any) {
		if (process.env.NODE_ENV === 'development') console.error('Error in getAllEmployeesAdmin saga:', error);
		if (error?.response?.status === 401 || error?.response?.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
			return;
		}
		// Handle 405 or other HTTP errors
		if (error?.response?.status === 405) {
			yield put(getAllEmployeesAdminFailure({
				status: 'error',
				message_text: 'Method not allowed. Please contact administrator.'
			}));
		} else if (error?.response?.data) {
			yield put(getAllEmployeesAdminFailure(error.response.data));
		} else {
			yield put(getAllEmployeesAdminFailure({
				status: 'error',
				message_text: error?.message || 'Network error. Please try again.'
			}));
		}
	}
}

export function* onApplyLeaveAdminStart(): Generator<any, void, any> {
	yield takeLatest(ApplyLeaveAdminActionType.APPLY_LEAVE_ADMIN_START as any, applyLeaveAdmin);
}

export function* onGetAllEmployeesAdminStart(): Generator<any, void, any> {
	yield takeLatest(
		ApplyLeaveAdminActionType.GET_ALL_EMPLOYEES_ADMIN_START as any,
		getAllEmployeesAdmin,
	);
}

export function* applyLeaveAdminSaga(): Generator<any, void, any> {
	yield all([fork(onApplyLeaveAdminStart), fork(onGetAllEmployeesAdminStart)]);
}
